import { createPortal } from 'react-dom';

/**
 * PortalModal - A reusable modal component that renders via React Portal
 * to ensure proper z-index stacking above all other content.
 * 
 * This solves the common issue where modals appear behind other UI elements
 * due to stacking context limitations.
 * 
 * Usage:
 * <PortalModal isOpen={showModal} onClose={() => setShowModal(false)}>
 *   <h2>Modal Title</h2>
 *   <p>Modal content...</p>
 * </PortalModal>
 */
export default function PortalModal({ 
  isOpen, 
  onClose, 
  children,
  maxWidth = 'max-w-lg',
  className = ''
}) {
  if (!isOpen) return null;
  
  return createPortal(
    <div 
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 99999 }}
      data-testid="portal-modal-overlay"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/90 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Content */}
      <div 
        className={`relative bg-zinc-900 border border-zinc-700 rounded-2xl w-full ${maxWidth} p-6 shadow-2xl max-h-[90vh] overflow-y-auto ${className}`}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="portal-modal-content"
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
